import { Box } from "@mui/material";
import { lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "react-keycloak-manager";
import { Route, useNavigate } from "react-router-dom";
import {
	EnumUtils,
	i18Vevy,
	ILogoProps,
	IMenuPage,
	MainMenu,
	MenuComposer,
	ProtectedElement,
	SuspenseRoutes,
	useSiteStatus,
} from "react-vevy-library";

import { ExtendedProfile } from "../interfaces/Entities";
import { UserLevel } from "../interfaces/Enums";
import GatesPage from "./GatesPage";
import WindowsPage from "./WindowsPage";
import logo from "/Logo.png";

const Splash = lazy(() => import("./Splash"));
const Users = lazy(() => import("./Users"));
const User = lazy(() => import("./User"));
const DebugPage = lazy(() => import("./DebugPage"));
const AlarmsPage = lazy(() => import("./AlarmsPage"));
const Calendars = lazy(() => import("./Calendars"));
const Calendar = lazy(() => import("./Calendar"));
const Groups = lazy(() => import("./GroupsPage"));
const Group = lazy(() => import("./Group"));

export default function Home() {
	const { t } = useTranslation();
	const { authenticatedUser, isAuthenticated } = useKeycloak();
	const [menu, setMenu] = useState<IMenuPage[]>([]);
	const [level, setLevel] = useState<UserLevel>(UserLevel.Guest);
	const [showGates, setShowGates] = useState(false);
	const [showAlarms, setShowAlarms] = useState(false);
	const [showWindows, setShowWindows] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const navigator = useNavigate();
	const siteStatus = useSiteStatus();

	useEffect(() => {
		if (isAuthenticated) {
			var extUser = authenticatedUser?.profile as ExtendedProfile;
			setShowGates(
				String(extUser?.VppGatesShow ?? "").toLowerCase() !== "hide",
			);
			setShowAlarms(
				String(extUser?.VppAlarmsShow ?? "").toLowerCase() !== "hide",
			);
			setShowWindows(
				String(extUser?.VppWindowsShow ?? "").toLowerCase() !== "hide",
			);
			var userLevel = extUser?.VppUserLevel ?? "guest";

			// Capitalize first letter to match enum keys (e.g. "admin" -> "Admin")
			// Only for retrocompatibility, ideally the stored value should already match the enum keys
			userLevel = userLevel.charAt(0).toUpperCase() + userLevel.slice(1);
			var level = EnumUtils.parseEnum(userLevel, UserLevel, UserLevel.Guest);
			setLevel(level);
			setIsAdmin(level === UserLevel.Admin);
		}
	}, [isAuthenticated, authenticatedUser]);

	useEffect(() => {
		if (isAuthenticated && !siteStatus.isLogged) {
			siteStatus.setIsLogged(isAuthenticated);
			navigator("/");
		}
	}, [isAuthenticated]);

	useEffect(() => {
		var menu = MenuComposer([
			{
				menuItem: { title: t("home.home"), link: "/" },
				enabled: () => !isAuthenticated,
			},
			{
				menuItem: {
					title: t("home.gates"),
					link: "/gate",
				},
				enabled: () => showGates,
			},
			{
				menuItem: {
					title: t("home.alarms"),
					link: "alarm",
				},
				enabled: () => showAlarms,
			},
			{
				menuItem: {
					title: t("home.windows"),
					link: "windows",
				},
				enabled: () => showWindows,
			},
			{
				menuItem: [
					{ title: t("home.users"), link: "/users" },
					{ title: t("home.debug"), link: "/debug" },
				],
				enabled: () => isAdmin,
			},
			{
				menuItem: [
					{
						title: t("home.calendars"),
						link: "calendars",
					},
					{
						title: t("home.groups"),
						link: "groups",
					},
				],
				enabled: () =>
					level === UserLevel.SuperUser || level === UserLevel.Admin,
			},
		]);
		setMenu(menu);
	}, [level, isAdmin, i18Vevy.language]);

	const logoHome: ILogoProps = {
		content: (
			<img src={logo} alt='Logo' style={{ width: "30px", height: "auto" }} />
		),
		link: "/",
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<MainMenu title='Vevy Passepartout' pages={menu} logo={logoHome} />
			<SuspenseRoutes>
				<Route path='/' element={<Splash />} />
				<Route
					path='gate'
					element={
						<ProtectedElement enabled={() => showGates}>
							<GatesPage />
						</ProtectedElement>
					}
				/>
				<Route
					path='alarm'
					element={
						<ProtectedElement enabled={() => showAlarms}>
							<AlarmsPage />
						</ProtectedElement>
					}
				/>
				<Route
					path='windows'
					element={
						<ProtectedElement enabled={() => showWindows}>
							<WindowsPage />
						</ProtectedElement>
					}
				/>
				<Route
					path='calendars'
					element={
						<ProtectedElement
							enabled={() =>
								level === UserLevel.SuperUser || level === UserLevel.Admin
							}
						>
							<Calendars />
						</ProtectedElement>
					}
				/>
				<Route
					path='calendar'
					element={
						<ProtectedElement
							enabled={() =>
								level === UserLevel.SuperUser || level === UserLevel.Admin
							}
						>
							<Calendar />
						</ProtectedElement>
					}
				/>
				<Route
					path='groups'
					element={
						<ProtectedElement
							enabled={() =>
								level === UserLevel.SuperUser || level === UserLevel.Admin
							}
						>
							<Groups />
						</ProtectedElement>
					}
				/>
				<Route
					path='group'
					element={
						<ProtectedElement
							enabled={() =>
								level === UserLevel.SuperUser || level === UserLevel.Admin
							}
						>
							<Group />
						</ProtectedElement>
					}
				/>
				<Route
					path='/users'
					element={
						<ProtectedElement enabled={() => isAdmin}>
							<Users />
						</ProtectedElement>
					}
				/>
				<Route
					path='/user'
					element={
						<ProtectedElement enabled={() => isAdmin}>
							<User />
						</ProtectedElement>
					}
				/>
				<Route
					path='/debug'
					element={
						<ProtectedElement enabled={() => isAdmin}>
							<DebugPage />
						</ProtectedElement>
					}
				/>
			</SuspenseRoutes>
		</Box>
	);
}
